'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations, useLocale } from 'next-intl';
import { createClient } from '@/utils/supabase/client';
import { Lock, Mail, LogIn, Loader2, ShieldCheck, AlertCircle } from 'lucide-react';
import PasswordInput from '@/app/components/PasswordInput';

export default function LoginPage() {
  const locale = useLocale(); 
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();
  
  // Usamos el namespace exacto que usted tenía en su LoginForm original
  const t = useTranslations('login'); 

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  
  // SOLUCIÓN ESTRUCTURAL: Guardar el mensaje final, no la llave dinámica.
  const [errorMessage, setErrorMessage] = useState(""); 
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    // Validaciones limpias traduciendo el error inmediatamente
    if (!email) {
      setErrorMessage(t('error_required_email'));
      setLoading(false);
      return;
    }
    if (!password) {
      setErrorMessage(t('error_required_password'));
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: password,
      });

      if (error) {
        throw error;
      }

      if (data.user) {
        if (!data.user.email_confirmed_at) {
          await supabase.auth.signOut();
          setErrorMessage(
            t('error_account_not_verified') ||
              'Cuenta pendiente de verificación. Revisa tu correo (y Spam) o reenvía el enlace.'
          );
          router.push(
            `/${locale}/verify?email=${encodeURIComponent(email.trim().toLowerCase())}&needVerify=1`
          );
          return;
        }

        // Heal lagging usuarios.verificado so proxy does not trap confirmed users on /verify.
        try {
          await fetch('/api/auth/sync-verification', { method: 'POST' });
        } catch (syncError) {
          console.warn('SYNC_VERIFICATION_CLIENT_FAILED', syncError);
        }

        const requestedNext = searchParams.get('next');
        const safeNext = requestedNext?.startsWith(`/${locale}/`)
          ? requestedNext
          : `/${locale}/dashboard`;
        router.push(safeNext);
        router.refresh(); 
      }
    } catch (error) {
      console.error('❌ Error de Login:', error.message);
      
      // Manejo de errores de Supabase mapeados a su diccionario
      if (error.message.includes('Invalid login credentials')) {
        setErrorMessage(t('error_invalid_credentials'));
      } else if (error.message.includes('Email not confirmed')) {
        router.push(`/${locale}/verify?email=${encodeURIComponent(email.trim().toLowerCase())}`);
      } else {
        setErrorMessage(error.message); 
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] font-sans selection:bg-cyan-500/30 p-4 relative overflow-hidden">
      
      {/* Efectos de Iluminación Neon de Fondo */}
      <div className="absolute inset-0 z-0 pointer-events-none flex justify-center items-center">
        <div className="absolute top-10 w-[600px] h-[600px] bg-cyan-600/10 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-10 w-[400px] h-[400px] bg-blue-600/10 rounded-full blur-[120px]"></div>
      </div>
      
      {/* Patrón de fondo sutil */}
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#06b6d4_1px,transparent_1px)] [background-size:20px_20px] z-0"></div>

      <div className="w-full max-w-md relative z-10">
        
        {/* Header superior */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-slate-900/80 backdrop-blur-md px-4 py-2 rounded-full border border-cyan-500/30 mb-6 shadow-[0_0_15px_rgba(6,182,212,0.2)]">
            <ShieldCheck className="w-5 h-5 text-cyan-400" />
            <span className="text-sm font-bold tracking-wide text-cyan-100">{t('secureLogin')}</span>
          </div>
          <h1 className="text-4xl font-black mb-2 text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
            {t('login_title')}
          </h1>
          <p className="text-slate-400 font-medium">
            {t('login_subtitle')}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-slate-900/60 backdrop-blur-xl p-8 rounded-3xl shadow-[0_0_40px_rgba(6,182,212,0.15)] border border-cyan-500/20 w-full mx-auto"
        >
          <div className="flex items-center justify-center mb-8">
            <div className="p-4 bg-cyan-900/30 rounded-full border border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.4)]">
              <Lock className="text-cyan-400 w-8 h-8" />
            </div>
          </div>
          
          <div className="mb-5">
            <label className="block text-cyan-200 text-sm font-bold mb-2" htmlFor="email">
              {t('email_label')}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-cyan-500/60" />
              </div>
              <input
                id="email"
                type="email"
                className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-[#0F172A]/80 text-white border border-slate-700/50 focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/30 transition-all outline-none placeholder:text-slate-600 font-medium"
                placeholder={t('email_placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-cyan-200 text-sm font-bold" htmlFor="password">
                {t('password_label')}
              </label>
              <Link href={`/${locale}/recovery`} className="text-xs font-bold text-cyan-500 hover:text-cyan-400 transition-colors">
                {t('forgot_password')}
              </Link>
            </div>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('password_placeholder')}
              autoComplete="current-password"
              showStrengthIndicator={false}
              disabled={loading}
            />
          </div>

          {/* RENDERIZADO SEGURO: Sin llaves dinámicas, solo el string puro */}
          {errorMessage && (
            <div className="mb-6 p-4 bg-red-950/50 border border-red-500/30 rounded-xl flex items-start gap-3 text-red-400 text-sm font-medium animate-in fade-in slide-in-from-top-2">
              <AlertCircle size={18} className="mt-0.5 shrink-0" /> 
              <span>{errorMessage}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-black text-lg shadow-[0_0_20px_rgba(6,182,212,0.4)] transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
          >
            {loading ? (
              <Loader2 className="animate-spin w-6 h-6 text-white" />
            ) : (
              <>
                <LogIn className="w-5 h-5" />
                {t('login_button')}
              </>
            )}
          </button>
        </form>

        <div className="text-center mt-8">
          <p className="text-sm text-slate-400">
            {t('register_prompt')}{' '}
            <Link href={`/${locale}/register`} className="font-bold text-cyan-400 hover:text-cyan-300 transition-colors">
              {t('register_link')}
            </Link>
          </p>
          
          <div className="mt-8">
            <Link href={`/${locale}`} className="text-sm font-bold text-slate-600 hover:text-cyan-500 transition-colors">
              &larr; {t('backToHome')}
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}


